import NoDataIcon from '@assets/jsx-icon/NoDataIcon';
import BaseLoading from '@components/BaseLoading';
import FilterRoot from '@components/filter/FilterRoot';
import IMultiSelectWithCheckBox from '@components/multi-select/IMultiSelectWithCheckBox';
import PopUpBase from '@components/PopUpBase';
import PopUpWarning from '@components/PopUpWarning';
import StyledAutocomplete from '@components/styled-autocomplete';
import useStyledAutocomplete from '@components/styled-autocomplete/useStyledAutocomplete';
import { pushError } from '@components/toast';
import { cameraController, locationController } from '@controllers/index';
import { Paging } from '@Core';
import { IGetCameraProps } from '@EcamModel/controllers/ICameraHttpController';
import { IGetLocationProps } from '@EcamModel/controllers/ILocationHttpController';
import { CameraWithInfo, Location, Status } from '@EcamModel/model';
import { Zones } from '@EcamModel/model/Zones';
import useMedia from '@hooks/useMedia';
import usePopUp, { IPopUp } from '@hooks/usePopUp';
import { Box, Button, Divider, Grid, Pagination, Radio, Stack, TextField, Typography } from '@mui/material';
import { useCameraDetailContext } from '@pages/cameras/hook/CameraDetailsProvider';
import { CameraSearchType, camerasSearchTypes, Filter } from '@pages/cameras/list';
import ListNoData from '@pages/cameras/list/components/NoDataCameras';
import SearchTypeSelect from '@pages/cameras/list/components/SearchTypeSelect';
import color from '@theme/Colors';
import { debounce } from 'lodash';
import { useCallback, useEffect, useState } from 'react';
import { FiSearch } from 'react-icons/fi';
import { useQuery } from 'react-query';
import { formatUKDate } from 'src/helpers';
import { useBackdrop } from 'src/providers/BackdropProvider';

type Props = IPopUp & {
    cameraId: number;
};

export default function PopUpSwapMac(props: Props) {
    const { open, onClose, cameraId } = props;
    const { isMobileSM } = useMedia();
    const { getDetailsCamera } = useCameraDetailContext();

    const backdrop = useBackdrop();

    const popUpConfirmSwap = usePopUp();

    const defaultFilter: Filter = {
        page: 1,
        searchType: camerasSearchTypes[0],
        search: '',
    };

    const [cameraIdSwap, setCameraIdSwap] = useState<number>();
    const [filter, setFilter] = useState(defaultFilter);
    const [zones, setZones] = useState<Zones[]>([]);
    const [cameras, setCameras] = useState<Paging<CameraWithInfo>>({
        page: 1,
        pageSize: 5,
        rows: [],
        total: 0,
        totalPages: 0,
    });
    const [isLoading, setIsLoading] = useState(true);
    const [searchValue, setSearchValue] = useState('');

    const getCameras = async () => {
        setIsLoading(true);
        const _filter: IGetCameraProps = {
            page: filter.page || 1,
            pageSize: 5,
            LocationId: Number(filter?.location?.Id) || undefined,
            ZoneIds: !!filter?.zones ? filter?.zones?.map((z) => z.Id!) : undefined,
            search: {
                fields: filter?.searchType?.Value === CameraSearchType.Name ? ['Name'] : [],
                content: filter?.searchType?.Value === CameraSearchType.Name ? filter?.search! : '',
            },
            Mac: filter?.searchType?.Value === CameraSearchType.Mac ? filter?.search! : undefined,
            filter: {
                Status: Status.Active,
                Id: {
                    $not: cameraId,
                },
            },
        };

        await cameraController
            .list(_filter)
            .then((res) => {
                setCameras(res);
            })
            .finally(() => setIsLoading(false));
    };

    const swapMac = async () => {
        if (!cameraId || !cameraIdSwap) {
            pushError('Not found this camera');
            return;
        }

        backdrop.setTrue();

        await cameraController
            .swapMacCamera({
                currentCameraId: cameraId,
                targetCameraId: cameraIdSwap,
            })
            .then(() => {
                getDetailsCamera();
            })
            .finally(() => {
                closePopup();
                backdrop.setFalse();
            });
    };

    const locationAutocomplete = useStyledAutocomplete({
        async getList(props: IGetLocationProps): Promise<Paging<any>> {
            const res = await locationController.list(props).finally(() => {});
            return res;
        },
        listProps: {
            search: { fields: ['name'] } as any,
        },
        dependencyList: [],
    });

    const onChangeLocation = (l?: Location) => {
        setFilter((prev) => ({ ...prev, location: l ?? undefined, zones: undefined, page: 1 }));
        setZones(l ? l.Zones! : []);
    };

    const resetCameraIdSwap = () => {
        setCameraIdSwap(undefined);
    };

    const resetFilter = () => {
        resetCameraIdSwap();
        setFilter(defaultFilter);
        setSearchValue('');
    };

    const closePopup = () => {
        props.onClose?.();
        resetFilter();
    };

    const debSearchClaim = useCallback(
        debounce((nextValue) => {
            setFilter((prev) => ({ ...prev, search: nextValue, page: 1 }));
        }, 500),
        [setFilter]
    );

    useEffect(() => {
        if (!open) {
            return;
        }
        getCameras();
    }, [open, filter.location, filter.zones, filter.page, filter.search]);

    useEffect(() => {
        if (filter.search) {
            getCameras();
        }
    }, [filter.searchType]);

    return (
        <PopUpBase
            open={open}
            onClose={onClose}
            fixOverflow
            dialogProps={{
                maxWidth: 'lg',
                PaperProps: {
                    style: { minWidth: isMobileSM ? 350 : 700, maxWidth: 700 },
                },
            }}
            title={<Typography variant="h4">Swap MAC camera</Typography>}
            desc={
                <Stack spacing={1}>
                    <FilterRoot
                        handleResetFilter={() => resetFilter()}
                        rightTitleContent={
                            <Box>
                                <TextField
                                    sx={{ '& .MuiOutlinedInput-root': { paddingLeft: 0 } }}
                                    fullWidth
                                    label="Search by"
                                    placeholder="Search..."
                                    onChange={(e) => {
                                        const value = e.target.value;
                                        if (value !== searchValue) {
                                            setSearchValue(value);
                                            debSearchClaim(value);
                                        }
                                    }}
                                    value={searchValue}
                                    InputProps={{
                                        startAdornment: (
                                            <Stack direction="row">
                                                <SearchTypeSelect
                                                    label="Search by"
                                                    minWidth={80}
                                                    listData={camerasSearchTypes}
                                                    value={filter.searchType!}
                                                    disabledLoading={isLoading}
                                                    keyEqual="Id"
                                                    keyLabel="Name"
                                                    handleChange={(e) => {
                                                        setFilter((prev) => ({ ...prev, searchType: e }));
                                                    }}
                                                />
                                                <Divider
                                                    orientation="vertical"
                                                    flexItem
                                                    sx={{
                                                        mx: '5px',
                                                        borderRightWidth: 1,
                                                        borderColor: color.grey600,
                                                    }}
                                                    variant="middle"
                                                />
                                            </Stack>
                                        ),
                                        endAdornment: (
                                            <FiSearch
                                                color="disabled"
                                                size={22}
                                                style={{ color: 'rgba(0, 0, 0, 0.38)', cursor: 'pointer' }}
                                            />
                                        ),
                                    }}
                                />
                            </Box>
                        }
                        filterChild={
                            <Grid container spacing={2}>
                                <Grid item xs={12}>
                                    <StyledAutocomplete
                                        {...locationAutocomplete}
                                        getOptionLabel={(o) => o.Name || ''}
                                        label="Location"
                                        value={filter.location}
                                        placeholder="All locations"
                                        disabled={isLoading}
                                        onChange={(l) => {
                                            onChangeLocation(l);
                                            resetCameraIdSwap();
                                        }}
                                    />
                                </Grid>

                                <Grid item xs={12}>
                                    <IMultiSelectWithCheckBox
                                        limitTags={3}
                                        label={'Zone'}
                                        keyId={'Id'}
                                        keyLabel={'PublicName'}
                                        placeHolder={'Select zone'}
                                        listData={zones}
                                        isDisable={!zones?.length || filter.location === undefined || isLoading}
                                        value={filter.zones ?? []}
                                        setFilter={(option) => {
                                            setFilter((prev) => ({
                                                ...prev,
                                                zones: option.length ? option : undefined,
                                                page: 1,
                                            }));
                                            resetCameraIdSwap();
                                        }}
                                        chipSize={150}
                                    />
                                </Grid>
                            </Grid>
                        }
                    />

                    <Stack
                        p={2}
                        bgcolor={color.grey100}
                        borderRadius={2}
                        spacing={2}
                        sx={{
                            height: '440px',
                            overflowY: 'auto',
                            '::-webkit-scrollbar': {
                                width: '2px',
                            },
                        }}
                    >
                        {isLoading ? (
                            <BaseLoading displayHeight={400} />
                        ) : (cameras?.rows?.length ?? 0) > 0 ? (
                            cameras?.rows?.map((e) => (
                                <Stack
                                    key={e.Id}
                                    spacing={2}
                                    direction={'row'}
                                    border={'1px solid #ddd'}
                                    p={2}
                                    borderRadius={2}
                                    bgcolor={color.white}
                                >
                                    <Box p={0}>
                                        <Radio
                                            value={e.Id}
                                            checked={e.Id === cameraIdSwap}
                                            onChange={() => setCameraIdSwap(e.Id)}
                                        />
                                    </Box>
                                    <Stack spacing={1} flex={1}>
                                        <Typography variant="h5">{e?.Name}</Typography>
                                        <Typography>MAC: {e?.Solar4gCamera?.Mac}</Typography>
                                        <Typography>Production: {formatUKDate(e?.DeployedDate)}</Typography>
                                    </Stack>
                                </Stack>
                            ))
                        ) : (
                            <Stack direction={'column'} alignItems={'center'} justifyContent={'center'} flex={1}>
                                <NoDataIcon />
                                <Typography sx={{ color: color.grey600 }} textAlign={'center'}>
                                    No data here
                                </Typography>
                            </Stack>
                        )}
                    </Stack>

                    {cameras?.totalPages! > 1 && !isLoading && (
                        <Stack direction={'row'} justifyContent="center">
                            <Pagination
                                count={cameras?.totalPages || 1}
                                page={cameras?.page}
                                onChange={(_, page) => {
                                    setFilter((prev) => ({ ...prev, page: page }));
                                    resetCameraIdSwap();
                                }}
                                variant="outlined"
                                shape="rounded"
                            />
                        </Stack>
                    )}

                    <PopUpWarning
                        {...popUpConfirmSwap}
                        message={`Are you sure you want to Swap MAC of this camera?`}
                        onConfirm={() => {
                            swapMac();
                            popUpConfirmSwap.onClose();
                        }}
                    />
                </Stack>
            }
            customActions={
                <Stack direction={'row'} width="100%" justifyContent="space-between">
                    <Button sx={{ minWidth: 130 }} variant="cancel" onClick={() => closePopup()}>
                        Cancel
                    </Button>

                    <Button
                        sx={{ minWidth: 130 }}
                        variant="contained"
                        disabled={!cameraIdSwap}
                        onClick={() => popUpConfirmSwap.setTrue()}
                    >
                        Swap
                    </Button>
                </Stack>
            }
            minWidthButton={150}
        />
    );
}
